import { Button } from "@/react/ui/button"
import { CampaignStatisticsTable } from "./stats/campaign-statistics-table"
import { Icon } from "~/react/ui/icon"
import { SectionHeader } from "~/react/ui/section-header"
import EmailCapacityBreakdown from "~/react/components/email_accounts/email_capacity_breakdown"
import { Card, CardContent } from "~/react/ui/card"
import { CurrencyHelper } from "~/helpers/currency_helper"
import { Metrics, MetricsItem, MetricsSeparator } from "~/react/ui/metrics"
import { AbTestingOverviewTable } from "./stats/ab-testing-overview-table"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuDivider,
} from "~/react/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogCloseButton,
  DialogBody,
  DialogFooter,
} from "~/react/ui/dialog"
import { useState, useRef } from "react"
import React from "react"

declare const i18n: {
  t: (key: string, options?: Record<string, unknown>) => string
}

interface FollowUp {
  name: string
  step: number
  subject: string
  pending: number
  messages_sent: number
  messages_delivered: number
  opens: number
  clicked: number
  replied: number
  bounced: number
  unsubscribed: number
  successful: number
  open_rate: number
  click_rate: number
  reply_rate: number
  bounce_rate: number
  unsubscribe_rate: number
  successful_rate: number
}

interface CampaignStatsProps {
  campaign: {
    name: string
    recipients_count: number
    pending_count: number
    sent_count: number
    delivered_count: number
    opened_count: number
    clicked_count: number
    replied_count: number
    bounced_count: number
    successful_count: number
    unsubscribed_recipients_count: number
    open_rate: number
    click_rate: number
    reply_rate: number
    bounce_rate: number
    unsubscribe_rate: number
    successful_rate: number
    tracked: boolean
    tracked_links: boolean
    introduction_message_subject: string
    started_at: string | null
  }
  followUps: FollowUp[]
  csvDownloadUrl: string
  unsubscriptionsPath: string
}

export default function CampaignStats({
  campaign,
  followUps,
  csvDownloadUrl,
  unsubscriptionsPath,
}: CampaignStatsProps) {
  const formatPercent = (rate: number) => `${(rate * 100).toFixed(1)}%`
  const formatCount = (count: number) => CurrencyHelper.numberWithDelimiter(count)

  const [selectedVersion, setSelectedVersion] = useState<string | null>(null)
  const [hasSufficientSample, setHasSufficientSample] = useState(true)
  const [pendingVersion, setPendingVersion] = useState<string | null>(null)
  const confirmationDialogRef = useRef<HTMLDialogElement>(null)

  const versionOptions = [
    { value: "version_a", label: i18n.t("js.campaigns.stats.index.version_a") },
    { value: "version_b", label: i18n.t("js.campaigns.stats.index.version_b") },
  ]

  const handleVersionSelect = (version: string) => {
    if (hasSufficientSample) {
      setSelectedVersion(version)
    } else {
      setPendingVersion(version)
      confirmationDialogRef.current?.showModal()
    }
  }

  const handleDialogClose = () => {
    const dialog = confirmationDialogRef.current
    if (!dialog) return

    if (dialog.returnValue === "confirm" && pendingVersion) {
      setSelectedVersion(pendingVersion)
      setPendingVersion(null)
    } else {
      setPendingVersion(null)
    }
  }

  const handleConfirmSelection = () => {
    if (confirmationDialogRef.current) {
      confirmationDialogRef.current.returnValue = "confirm"
      confirmationDialogRef.current.close()
    }
  }

  const handleCancelSelection = () => {
    if (confirmationDialogRef.current) {
      confirmationDialogRef.current.returnValue = "cancel"
      confirmationDialogRef.current.close()
    }
  }

  return (
    <>
      <SectionHeader
        title="Sequence overview"
        actions={
          <Button size="sm" asChild>
            <a href={csvDownloadUrl}>
              <Icon icon="download" size="sm" aria-hidden="true" />
              {i18n.t("js.campaigns.stats.index.download_report")}
            </a>
          </Button>
        }
      />

      <div className="campaign-stats__sequence-overview d-flex flex-column flex-lg-row gap-4 mb-8">
        <Card
          variant="widget"
          grow
          className="flex-fill campaign-stats__sequence-overview-card campaign-stats__sequence-overview-card--capacity"
        >
          <CardContent>
            <EmailCapacityBreakdown
              sent={campaign.sent_count}
              scheduled={campaign.pending_count}
              limit={campaign.sent_count + campaign.pending_count}
              showLegend
              showPercentages
              className="mt-2"
            />
          </CardContent>
        </Card>

        <Card
          variant="widget"
          grow
          className="flex-fill campaign-stats__sequence-overview-card campaign-stats__sequence-overview-card--metrics"
        >
          <CardContent>
            <Metrics>
              <MetricsItem
                icon="eye"
                iconColor="var(--colors-success-600)"
                label={i18n.t("js.sending_status.opened")}
                value={campaign.tracked ? formatCount(campaign.opened_count) : "-"}
                secondaryValue={campaign.tracked ? formatPercent(campaign.open_rate) : null}
              />
              <MetricsSeparator />
              <MetricsItem
                icon="hand-pointer"
                iconColor="var(--colors-cyan-500)"
                label={i18n.t("js.sending_status.clicked")}
                value={campaign.tracked_links ? formatCount(campaign.clicked_count) : "-"}
                secondaryValue={campaign.tracked_links ? formatPercent(campaign.click_rate) : null}
              />
              <MetricsSeparator />
              <MetricsItem
                icon="reply"
                iconColor="var(--colors-violette-500)"
                label={i18n.t("js.sending_status.replied")}
                value={formatCount(campaign.replied_count)}
                secondaryValue={formatPercent(campaign.reply_rate)}
              />
              <MetricsSeparator />
              <MetricsItem
                icon="thumbs-up"
                iconColor="var(--colors-emerald-500)"
                label={i18n.t("js.sending_status.successful")}
                value={formatCount(campaign.successful_count)}
                secondaryValue={formatPercent(campaign.successful_rate)}
              />
            </Metrics>
          </CardContent>
        </Card>
      </div>

      <SectionHeader
        title="A/B testing overview"
        subtitle={i18n.t("js.campaigns.stats.index.ab_testing_subtitle")}
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm">
                {i18n.t("js.campaigns.stats.index.manage_ab_testing")}
                <Icon icon="chevron-down" size="xs" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {versionOptions.map((option) => (
                <DropdownMenuItem key={option.value} onClick={() => handleVersionSelect(option.value)} closeOnClick>
                  {option.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuDivider />
              <DropdownMenuItem className="campaign-stats__choose-winning-version-footnote-item">
                <p className="campaign-stats__choose-winning-version-footnote">
                  {i18n
                    .t("js.campaigns.stats.index.choose_winning_version_footnote")
                    .split("\n")
                    .map((line, index, array) => (
                      <React.Fragment key={index}>
                        {line}
                        {index < array.length - 1 && <br />}
                      </React.Fragment>
                    ))}
                </p>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      <Card density="compact" className="mb-8">
        <CardContent>
          <AbTestingOverviewTable onSampleSizeChange={setHasSufficientSample} selectedVersion={selectedVersion} />
        </CardContent>
      </Card>

      <Dialog ref={confirmationDialogRef} onClose={handleDialogClose}>
        <DialogContent>
          <form method="dialog">
            <DialogHeader>
              <DialogTitle>{i18n.t("js.campaigns.stats.index.confirm_winning_version_title")}</DialogTitle>
              <DialogCloseButton />
            </DialogHeader>
            <DialogBody>
              <p
                dangerouslySetInnerHTML={{
                  __html: i18n.t("js.campaigns.stats.index.confirm_winning_version_content_html", {
                    version: pendingVersion
                      ? versionOptions.find((opt) => opt.value === pendingVersion)?.label || pendingVersion
                      : "",
                  }),
                }}
              />
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={handleCancelSelection} value="cancel">
                {i18n.t("js.campaigns.stats.index.cancel")}
              </Button>
              <Button type="button" variant="primary" onClick={handleConfirmSelection} value="confirm">
                {i18n.t("js.campaigns.stats.index.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <SectionHeader title="Metrics breakdown" />

      <CampaignStatisticsTable campaign={campaign} followUps={followUps} unsubscriptionsPath={unsubscriptionsPath} />
    </>
  )
}
